import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, CircularProgress, TextField, Tooltip } from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

const iconStyles = { position: 'absolute', right: 12, top: 14 };

const MAutocomplete = props => {
  const {
    blurOnSelect,
    disableClearable,
    error,
    getOptionLabel,
    hasSearched,
    helperText,
    id,
    inputIcon,
    inputValue,
    label,
    loading,
    popupIcon,
    name,
    options,
    value,
    defaultValue,
    onChange,
    onInputChange,
    openOnFocus,
    disabled,
    required,
    sx,
    classNames,
  } = props;
  const getAutocompleteValue = () => options.find(arrEl => arrEl.value === value);
  const autoCompleteValue = getAutocompleteValue() || defaultValue;
  const isValueEmpty =
    autoCompleteValue === null ||
    autoCompleteValue === undefined ||
    autoCompleteValue === '' ||
    Number.isNaN(autoCompleteValue);

  return (
    <Autocomplete
      blurOnSelect={blurOnSelect}
      className={classNames}
      clearIcon={<CancelRoundedIcon sx={{ color: 'graphic.dark' }} />}
      data-testid={name}
      disableClearable={disableClearable}
      disabled={disabled || !options.length}
      getOptionLabel={getOptionLabel ?? (option => option.label ?? '')}
      id={id}
      inputValue={inputValue}
      isOptionEqualToValue={(option, selected) => option?.value === selected?.value}
      loading={loading}
      name={name}
      onChange={onChange}
      onInputChange={onInputChange}
      openOnFocus={openOnFocus}
      options={options}
      popupIcon={popupIcon ?? null}
      renderInput={params => (
        <>
          <TextField
            {...params}
            error={error}
            fullWidth
            helperText={helperText}
            label={label}
            required={required}
            slotProps={{
              ...params.slotProps,
              htmlInput: {
                ...params.inputProps,
                ...(params.slotProps?.htmlInput || {}),
                autoComplete: 'off',
              },
              input: {
                ...params.InputProps,
                ...(params.slotProps?.input || {}),
              },
            }}
            variant="outlined"
          />
          {loading ? (
            <CircularProgress size={20} sx={{ color: 'secondary.main', ...iconStyles }} />
          ) : hasSearched && !options.length ? (
            <Tooltip title="No options available">
              <InfoRoundedIcon sx={{ color: 'graphic.main', ...iconStyles }} />
            </Tooltip>
          ) : isValueEmpty ? (
            inputIcon
          ) : null}
        </>
      )}
      sx={{
        position: 'relative',
        ...sx,
      }}
      value={autoCompleteValue}
    />
  );
};

MAutocomplete.propTypes = {
  blurOnSelect: PropTypes.bool,
  classNames: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
  disableClearable: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  getOptionLabel: PropTypes.func,
  hasSearched: PropTypes.bool,
  helperText: PropTypes.string,
  id: PropTypes.string.isRequired,
  inputIcon: PropTypes.element,
  inputValue: PropTypes.string,
  label: PropTypes.string,
  loading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  noOptionsText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  openOnFocus: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  popupIcon: PropTypes.element,
  required: PropTypes.bool,
  sx: PropTypes.shape({}),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
};

MAutocomplete.defaultProps = {
  blurOnSelect: false,
  classNames: '',
  defaultValue: null,
  disableClearable: false,
  disabled: false,
  error: false,
  getOptionLabel: undefined,
  hasSearched: true,
  helperText: '',
  inputIcon: null,
  inputValue: undefined,
  label: undefined,
  loading: true,
  noOptionsText: '',
  onInputChange: undefined,
  openOnFocus: false,
  popupIcon: null,
  required: false,
  sx: {},
  value: null,
};

export default MAutocomplete;
